// Shared Files-library picker for the assistant, native message, and community
// composers. The owning controller keeps attachment staging and submission.
export default class ComposerFilePicker {
    constructor(controller) {
        this.controller = controller
        this._attachRequestSeq = 0
        this._attachRows = []
        this._attachActiveIndex = -1
        this._attachOpener = null
    }

    disconnect() {
        clearTimeout(this._attachDebounce)
        // Ignore responses arriving after the owning composer disconnects.
        this._attachRequestSeq++
        this._attachRows = []
        this._attachOpener = null
    }

    // Opens the library picker over the same JSON the Files app itself
    // renders from (GET /user_files for the initial list, GET
    // /apps/files/search once the user types) rather than a parallel query
    // path — see FILES_FEATURE_SPEC.md.
    openFilePicker() {
        if (!this.controller.hasAttachPanelTarget) return
        const opening = this.controller.attachPanelTarget.hidden
        this.controller.attachPanelTarget.hidden = !opening
        if (opening) {
            this._attachOpener = document.activeElement
            this.controller.attachSearchInputTarget.value = ""
            this._fetchLibraryFiles("")
            this.controller.attachSearchInputTarget.focus()
        }
    }

    // Returns focus to whatever opened the picker (the "Choose from Files"
    // menu item) — matches list-switcher/universal-search's focus handling.
    closeAttachPanel() {
        if (!this.controller.hasAttachPanelTarget) return
        this.controller.attachPanelTarget.hidden = true
        const opener = this._attachOpener
        this._attachOpener = null
        if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus()
    }

    searchFiles() {
        clearTimeout(this._attachDebounce)
        const q = this.controller.attachSearchInputTarget.value.trim()
        // Back to Loading right away, not only once the debounced fetch below
        // resolves: the debounce protects the network call, not the displayed
        // list. Without this, arrow keys and Enter could act on a row set
        // that's already been superseded by the in-flight query, silently
        // attaching whichever file the OLD list had selected once the new
        // results swap in underneath it.
        this._showAttachLoading()
        this._attachDebounce = setTimeout(() => this._fetchLibraryFiles(q), 200)
    }

    // Arrow keys move the active row, Enter attaches it (or the sole result
    // if none is highlighted yet), Escape clears a query first and only
    // closes the panel on a second press — same two-tier behavior as
    // list-switcher's inputKeydown. The first tier stops propagation so the
    // surrounding split-menu's own Escape handler (which would otherwise
    // also collapse the whole + menu) only fires on the second, "really
    // closing" press.
    attachSearchKeydown(event) {
        if (event.key === "ArrowDown") {
            event.preventDefault()
            this._moveAttachActive(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._moveAttachActive(-1)
        } else if (event.key === "Enter") {
            event.preventDefault()
            const active = this._attachRows[this._attachActiveIndex] || (this._attachRows.length === 1 ? this._attachRows[0] : null)
            if (active) active.click()
        } else if (event.key === "Escape") {
            if (this.controller.attachSearchInputTarget.value !== "") {
                event.preventDefault()
                event.stopPropagation()
                this.controller.attachSearchInputTarget.value = ""
                this.searchFiles()
            } else {
                this.closeAttachPanel()
            }
        }
    }

    // Loading is the panel's one "nothing on screen is actionable yet" state,
    // so it has to drop the keyboard-nav row list together with the rows it
    // erases AND invalidate any request already in flight. Bumping the
    // request seq here rather than only in _fetchLibraryFiles is what closes
    // the flake: an earlier query's response can land during the 200ms
    // debounce window, and without the bump it passes the staleness check and
    // paints rows the pending fetch is about to erase — leaving _attachRows
    // pointing at detached buttons whose Stimulus click action has already
    // been unbound, so Enter attaches nothing at all.
    _showAttachLoading() {
        this._attachRequestSeq++
        this._attachRows = []
        this._attachActiveIndex = -1
        this.controller.attachResultsTarget.innerHTML = `<p class="composer-attach-loading">Loading…</p>`
    }

    _fetchLibraryFiles(q) {
        this._showAttachLoading()
        const seq = this._attachRequestSeq
        const isSearch = q.length >= 2
        const url = isSearch
            ? `${this.controller.filesSearchUrlValue}?q=${encodeURIComponent(q)}`
            : this.controller.filesIndexUrlValue
        fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.json() : {}))
            .then((data) => {
                if (seq !== this._attachRequestSeq) return
                this._renderLibraryResults(isSearch ? (data.results || []) : (data.files || []), { isSearch, query: q })
            })
            .catch(() => {
                if (seq !== this._attachRequestSeq) return
                this._renderLibraryResults([], { isSearch, query: q })
            })
    }

    _renderLibraryResults(rows, { isSearch = false, query = "" } = {}) {
        const container = this.controller.attachResultsTarget
        container.innerHTML = ""
        this._attachRows = []
        this._attachActiveIndex = -1
        if (rows.length === 0) {
            const empty = document.createElement("p")
            empty.className = "composer-attach-empty"
            empty.textContent = isSearch ? `No files match "${query}".` : "No files found."
            container.appendChild(empty)
            return
        }
        rows.forEach((row) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = "composer-attach-row"
            btn.setAttribute("role", "option")
            btn.setAttribute("aria-selected", "false")
            btn.dataset.action = `click->${this.controller.identifier}#pickLibraryFile`
            btn.dataset.id = row.id
            btn.dataset.name = row.name
            if (row.thumb_url) btn.dataset.thumbUrl = row.thumb_url
            const icon = document.createElement("i")
            icon.className = "bi bi-file-earmark"
            icon.setAttribute("aria-hidden", "true")
            const body = document.createElement("span")
            body.className = "composer-attach-row-body"
            const name = document.createElement("span")
            name.className = "composer-attach-row-name"
            name.textContent = row.name
            body.appendChild(name)
            if (row.snippet) {
                const snippet = document.createElement("span")
                snippet.className = "composer-attach-row-snippet"
                snippet.textContent = row.snippet
                body.appendChild(snippet)
            }
            btn.appendChild(icon)
            btn.appendChild(body)
            container.appendChild(btn)
            this._attachRows.push(btn)
        })
        // Preselect the top hit once the user is actively searching, so
        // Enter attaches it immediately; browsing the untouched list leaves
        // nothing selected (mirrors list-switcher's same idle-vs-typed cut).
        if (isSearch) this._setAttachActive(0)
    }

    _moveAttachActive(delta) {
        const rows = this._attachRows
        if (!rows.length) return
        let next = this._attachActiveIndex + delta
        if (next < 0) next = rows.length - 1
        if (next >= rows.length) next = 0
        this._setAttachActive(next)
    }

    _setAttachActive(index) {
        this._attachActiveIndex = index
        this._attachRows.forEach((row, i) => {
            const active = i === index
            row.classList.toggle("is-active", active)
            row.setAttribute("aria-selected", active ? "true" : "false")
        })
        const active = this._attachRows[index]
        if (active) active.scrollIntoView({ block: "nearest" })
    }

}
